/**
 * The published biblical calendar, read from PocketBase.
 *
 * Torahly generates the calendar and publishes one record per (year, convention) into
 * `calendar_years`. Covellah's Android client reads exactly this (see
 * `calendar/ServerCalendar.kt`) and does no calendar maths of its own; neither does this.
 * Torahly owns the calendar, everything else syncs.
 *
 * The collection is publicly readable, so the masthead renders before anyone signs in.
 */

import { pb } from './pb'

export interface PublishedDay {
  /** Gregorian date, "YYYY-MM-DD". */
  d: string
  /** Biblical month, 1-12. */
  m: number
  /** Day within the month. Months run 30 to 33 days. */
  dy: number
  /** Day of week, 1-7. 7 is the Sabbath. */
  w: number
  /** Day of year. */
  doy: number
  /** Feast name, absent on ordinary days. */
  f?: string
  /** Published Sabbath flag. Absent in practice: see isSabbath below. */
  s?: boolean
}

/**
 * Names match Torahly's YearStartOption exactly, because they are sent verbatim as the
 * `year_start_option` filter value.
 */
export type YearStartOption = 'WEDNESDAY' | 'YAHS_ENOCH'

/** The app's default, and so this one's. There is no UI to change it yet. */
export const DEFAULT_YEAR_START: YearStartOption = 'WEDNESDAY'

const BASE = 'https://api.torahly.app'
const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

/**
 * The Sabbath is the seventh day, read off the weekday.
 *
 * It does NOT read the `s` flag, even though the published shape carries one and the Android
 * client trusts it. No published day in the live data has `s` set to true, so anything keyed
 * on that flag silently believes the Sabbath never comes. Weekday 7 is the same answer the
 * generator itself used to lay the week out, and it is actually populated.
 */
export function isSabbath(day: PublishedDay): boolean {
  return day.s === true || day.w === 7
}

interface CachedYear {
  fetchedAt: number
  days: PublishedDay[]
}

function cacheKey(year: number, option: YearStartOption): string {
  return `covella.calendar.${year}.${option}`
}

function readCache(year: number, option: YearStartOption): CachedYear | null {
  try {
    const raw = localStorage.getItem(cacheKey(year, option))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedYear
    return Array.isArray(parsed.days) ? parsed : null
  } catch {
    return null
  }
}

function writeCache(year: number, option: YearStartOption, days: PublishedDay[]): void {
  try {
    localStorage.setItem(cacheKey(year, option), JSON.stringify({ fetchedAt: Date.now(), days }))
  } catch {
    /* a full or disabled localStorage costs a refetch, nothing more */
  }
}

/**
 * Cache-first with a 7-day refresh, matching the app. A failed refetch keeps serving whatever
 * was cached: a calendar a week stale is still the right calendar, and the alternative is a
 * masthead that empties itself whenever the network hiccups.
 */
export async function loadYear(
  year: number,
  option: YearStartOption = DEFAULT_YEAR_START,
): Promise<PublishedDay[]> {
  const cached = readCache(year, option)
  if (cached && Date.now() - cached.fetchedAt < CACHE_MAX_AGE_MS) return cached.days

  try {
    const filter = encodeURIComponent(`(year=${year} && year_start_option="${option}")`)
    const response = await fetch(`${BASE}/api/collections/calendar_years/records?filter=${filter}`)
    if (!response.ok) return cached?.days ?? []
    const body = (await response.json()) as { items?: { data?: { days?: PublishedDay[] } }[] }
    const days = body.items?.[0]?.data?.days
    if (!Array.isArray(days)) return cached?.days ?? []
    writeCache(year, option, days)
    return days
  } catch {
    return cached?.days ?? []
  }
}

/** Local calendar date as "YYYY-MM-DD". Never toISOString, which would shift by the offset. */
export function localDateKey(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export interface Reckoning {
  today: PublishedDay
  /** Every day of the biblical month today falls in, in order. */
  month: PublishedDay[]
  /** The next Sabbath, today included when today is one. */
  nextSabbath: PublishedDay | null
  /** Whole days from today to that Sabbath. 0 means today. */
  daysToSabbath: number | null
  /** The next few feasts, one entry per feast rather than per day. */
  upcomingFeasts: PublishedDay[]
}

export function reckon(days: PublishedDay[], todayKey = localDateKey()): Reckoning | null {
  const today = days.find((day) => day.d === todayKey)
  if (!today) return null

  // Only this month's own days. `days` can span several years now (the Calendar page loads three),
  // and the same month number recurs in each, so walk out from today while the month holds.
  const at = days.indexOf(today)
  let from = at
  let to = at
  while (from > 0 && days[from - 1]!.m === today.m) from--
  while (to < days.length - 1 && days[to + 1]!.m === today.m) to++
  const month = days.slice(from, to + 1)

  const ahead = days.filter((day) => day.d >= todayKey).sort((a, b) => a.d.localeCompare(b.d))
  const nextSabbath = ahead.find(isSabbath) ?? null

  const upcomingFeasts: PublishedDay[] = []
  const seen = new Set<string>()
  for (const day of ahead) {
    if (!day.f || seen.has(day.f)) continue
    seen.add(day.f)
    upcomingFeasts.push(day)
    if (upcomingFeasts.length === 3) break
  }

  return {
    today,
    month,
    nextSabbath,
    daysToSabbath: nextSabbath ? dayGap(todayKey, nextSabbath.d) : null,
    upcomingFeasts,
  }
}

/** Whole days between two "YYYY-MM-DD" keys, counted on the dates themselves so a daylight
 *  saving change cannot turn six days into five and three quarters. */
export function dayGap(fromKey: string, toKey: string): number {
  const from = new Date(`${fromKey}T00:00:00`)
  const to = new Date(`${toKey}T00:00:00`)
  return Math.round((to.getTime() - from.getTime()) / 86400000)
}

const ONES = [
  '',
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
  'eleventh',
  'twelfth',
  'thirteenth',
  'fourteenth',
  'fifteenth',
  'sixteenth',
  'seventeenth',
  'eighteenth',
  'nineteenth',
]

const TENS = ['', '', 'twentieth', 'thirtieth']
const TENS_PREFIX = ['', '', 'twenty', 'thirty']

/** "eighteenth", "twenty-first". Months run to 33 days, so this needs to reach past thirty. */
export function ordinalWord(n: number): string {
  if (n < 20) return ONES[n] ?? String(n)
  const tens = Math.floor(n / 10)
  const ones = n % 10
  if (ones === 0) return TENS[tens] ?? String(n)
  return `${TENS_PREFIX[tens]}-${ONES[ones]}`
}

/** How the date is said aloud, which is how it should be read. */
export function spokenDate(day: PublishedDay): string {
  return `the ${ordinalWord(day.dy)} day of the ${ordinalWord(day.m)} month`
}

const WEEKDAY_NAMES = [
  '',
  'First day',
  'Second day',
  'Third day',
  'Fourth day',
  'Fifth day',
  'Sixth day',
  'Sabbath',
]

export function weekdayName(day: PublishedDay): string {
  return WEEKDAY_NAMES[day.w] ?? ''
}


/* ------------------------------------------------------------------------------------------ */
/* The Calendar page: the same year, feasts and sunset reckoning as Torahly's web calendar.   */
/* ------------------------------------------------------------------------------------------ */

/*
 * Everything below reads what Torahly publishes and computes nothing of its own: the days come
 * from `calendar_years` (above), the feasts from the same feasts.json the Torahly site ships, and
 * the sunset from sunrise-sunset.org, which is the source Torahly's site and app use. Keep it in
 * step with website/webapp/src/calendar.ts in the Torahly repo.
 */

export const YEAR_START: Record<YearStartOption, { label: string; detail: string }> = {
  WEDNESDAY: {
    label: 'On/After Equinox',
    detail: 'Day 1 is the first Wednesday on or after the spring equinox. Kept by George Nuber and Hanging On His Words.',
  },
  YAHS_ENOCH: {
    label: 'Nearest Equinox',
    detail: 'Day 1 is the Wednesday nearest the spring equinox, even when it falls a day or two before it. Kept by Minten.',
  },
}

export interface Feast {
  id: number
  name: string
  month: number
  startDay: number
  endDay: number
  description: string
  verses: string[]
  isSabbath: boolean
  sabbathDays: number[]
}

export interface Location {
  name: string
  lat: number
  lon: number
  tz?: string
}

export const JERUSALEM: Location = { name: 'Jerusalem', lat: 31.7683, lon: 35.2137, tz: 'Asia/Jerusalem' }

const KEY_LOCATION = 'covella.location'
const KEY_YEAR_START = 'covella.yearStart'

function readSetting(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
function writeSetting(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* a blocked localStorage costs the choice, not the page */
  }
}

export function yearStart(): YearStartOption {
  return readSetting(KEY_YEAR_START) === 'YAHS_ENOCH' ? 'YAHS_ENOCH' : DEFAULT_YEAR_START
}
export function setYearStart(option: YearStartOption): void {
  writeSetting(KEY_YEAR_START, option)
}
export function location(): Location {
  try {
    const raw = readSetting(KEY_LOCATION)
    return raw ? (JSON.parse(raw) as Location) : JERUSALEM
  } catch {
    return JERUSALEM
  }
}
export function setLocation(next: Location): void {
  writeSetting(KEY_LOCATION, JSON.stringify(next))
}

/**
 * Pulls the year-start reading and the location the member chose on the Torahly web app, from
 * their owner-only `user_settings` record, into this browser's settings. Read only: the record
 * holds a dozen other keys that are Torahly's to write, so nothing goes back. Any failure
 * (signed out, no record, offline) leaves the local choice as it was.
 */
export async function pullAccountSettings(ownerId: string): Promise<void> {
  try {
    const r = await pb
      .collection('user_settings')
      .getFirstListItem<{ data?: Record<string, string> }>(`owner="${ownerId}"`)
    const data = r.data ?? {}
    if (data['torahly.yearStart'] === 'WEDNESDAY' || data['torahly.yearStart'] === 'YAHS_ENOCH') {
      writeSetting(KEY_YEAR_START, data['torahly.yearStart'])
    }
    const loc = data['torahly.location']
    if (loc) {
      const parsed = JSON.parse(loc) as Location
      if (typeof parsed.lat === 'number' && typeof parsed.lon === 'number') writeSetting(KEY_LOCATION, loc)
    }
  } catch {
    /* the local choice stands */
  }
}

/** The biblical year straddles two Gregorian years, so three are loaded and merged. */
export async function loadDays(option: YearStartOption = yearStart()): Promise<PublishedDay[]> {
  const year = new Date().getFullYear()
  const [a, b, c] = await Promise.all([
    loadYear(year - 1, option),
    loadYear(year, option),
    loadYear(year + 1, option),
  ])
  const seen = new Map<string, PublishedDay>()
  for (const day of [...a, ...b, ...c]) if (!seen.has(day.d)) seen.set(day.d, day)
  return [...seen.values()].sort((x, y) => x.d.localeCompare(y.d))
}

let feastsPromise: Promise<Feast[]> | null = null
export function loadFeasts(): Promise<Feast[]> {
  if (!feastsPromise) {
    feastsPromise = fetch(`${import.meta.env.BASE_URL}calendar/feasts.json`)
      .then((r) => (r.ok ? (r.json() as Promise<Feast[]>) : []))
      .catch(() => [])
  }
  return feastsPromise
}

export function feastsOn(feasts: Feast[], day: PublishedDay): Feast[] {
  return feasts.filter((f) => f.month === day.m && day.dy >= f.startDay && day.dy <= f.endDay)
}

export interface SunTimes {
  sunrise: Date
  sunset: Date
}
const sunCache = new Map<string, Promise<SunTimes | null>>()

/** sunrise-sunset.org, keyless and CORS-enabled, the same source Torahly uses. Cached per day. */
export function loadSun(loc: Location, dateKey = localDateKey()): Promise<SunTimes | null> {
  const id = `${loc.lat.toFixed(3)},${loc.lon.toFixed(3)},${dateKey}`
  let promise = sunCache.get(id)
  if (!promise) {
    promise = (async () => {
      try {
        const raw = readSetting(`covella.sun.${id}`)
        if (raw) {
          const c = JSON.parse(raw) as { sunrise: string; sunset: string }
          return { sunrise: new Date(c.sunrise), sunset: new Date(c.sunset) }
        }
        const r = await fetch(
          `https://api.sunrise-sunset.org/json?lat=${loc.lat}&lng=${loc.lon}&date=${dateKey}&formatted=0`,
        )
        if (!r.ok) return null
        const body = (await r.json()) as { status: string; results: { sunrise: string; sunset: string } }
        if (body.status !== 'OK') return null
        const out = { sunrise: new Date(body.results.sunrise), sunset: new Date(body.results.sunset) }
        writeSetting(`covella.sun.${id}`, JSON.stringify(out))
        return out
      } catch {
        return null
      }
    })()
    sunCache.set(id, promise)
  }
  return promise
}

export interface Today {
  day: PublishedDay
  afterSunset: boolean
  sun: SunTimes | null
  location: Location
}

/** Today by the biblical reckoning: after sunset at the location, it is already tomorrow. */
export async function reckonToday(days: PublishedDay[]): Promise<Today | null> {
  const loc = location()
  const now = new Date()
  const sun = await loadSun(loc)
  const afterSunset = !!sun && now.getTime() >= sun.sunset.getTime()
  const target = new Date(now)
  if (afterSunset) target.setDate(target.getDate() + 1)
  const day = days.find((d) => d.d === localDateKey(target))
  if (!day) return null
  return { day, afterSunset, sun, location: loc }
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`
}

/** 1 Enoch 72: parts of day and night per month. */
export const ENOCH_PARTS: Record<number, [number, number]> = {
  1: [10, 8], 2: [11, 7], 3: [12, 6], 4: [11, 7], 5: [10, 8], 6: [9, 9],
  7: [8, 10], 8: [7, 11], 9: [6, 12], 10: [7, 11], 11: [8, 10], 12: [9, 9],
}
export const ENOCH_PORTAL: Record<number, string> = {
  1: 'fourth', 2: 'fifth', 3: 'sixth', 4: 'sixth', 5: 'fifth', 6: 'fourth',
  7: 'third', 8: 'second', 9: 'first', 10: 'first', 11: 'second', 12: 'third',
}
export const ENOCH_VERSE: Record<number, string> = {
  1: '1 Enoch 72:8-10', 2: '1 Enoch 72:11-12', 3: '1 Enoch 72:13-14', 4: '1 Enoch 72:15-16',
  5: '1 Enoch 72:17-18', 6: '1 Enoch 72:19-20', 7: '1 Enoch 72:21-22', 8: '1 Enoch 72:23-24',
  9: '1 Enoch 72:25-26', 10: '1 Enoch 72:27-28', 11: '1 Enoch 72:29-30', 12: '1 Enoch 72:31-32',
}
/** The four leaders of the seasons (1 Enoch 82:11-18), who rise on the Day of Remembrance. */
export const SEASON_LEADERS: Record<number, { name: string; season: string }> = {
  1: { name: 'Melkejal', season: 'spring' },
  4: { name: 'Helemmelek', season: 'summer' },
  7: { name: 'Melejal', season: 'autumn' },
  10: { name: 'Narel', season: 'winter' },
}

/** Sunset is shown on the location's own clock, since a Jerusalem sunset read on another clock only confuses. */
export function fmtTime(d: Date, loc: Location = location()): string {
  try {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: loc.tz })
  } catch {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  }
}

/** Open-Meteo's keyless geocoder, the one Torahly uses. */
export async function geocode(q: string): Promise<Location[]> {
  const r = await fetch(
    `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=6&language=en&format=json`,
  )
  if (!r.ok) return []
  const body = (await r.json()) as {
    results?: { name: string; admin1?: string; country?: string; latitude: number; longitude: number; timezone?: string }[]
  }
  return (body.results ?? []).map((x) => ({
    name: [x.name, x.admin1, x.country].filter(Boolean).join(', '),
    lat: x.latitude,
    lon: x.longitude,
    tz: x.timezone,
  }))
}
